import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, CalendarDays } from "lucide-react";
import { Reveal } from "./Reveal";
import { LIFE_OPTIONS } from "../../data";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 font-body text-sm text-white placeholder:text-white/30 focus:border-[#d4af37] focus:outline-none transition-colors duration-300";

export const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", coverage_type: "", state: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.coverage_type) {
      toast.error("Please fill in your name, phone, email, and coverage type.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Quote request received! We'll reach out within one business day.");
      setForm({ name: "", phone: "", email: "", coverage_type: "", state: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-form-section" className="relative py-20 md:py-28 bg-[#0a192f] border-t border-[#bf953f]/10">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37] text-center">Free Quote</p>
          <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white text-center">
            Request your <span className="text-gold-gradient italic">quote.</span>
          </h2>
          <p className="mt-5 mx-auto max-w-xl text-center font-body font-light text-base md:text-lg text-white/60">
            Tell us a little about yourself and we'll match you with the right coverage — no pressure, no obligation.
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <form onSubmit={submit} className="mt-12 rounded-3xl border border-[#bf953f]/25 bg-black/30 p-8 md:p-10 space-y-5" data-testid="quote-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <input data-testid="quote-name-input" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
              <input data-testid="quote-phone-input" className={inputCls} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <input data-testid="quote-email-input" type="email" className={inputCls} placeholder="Email address *" value={form.email} onChange={set("email")} />
              <input data-testid="quote-state-input" className={inputCls} placeholder="State (e.g. Georgia)" value={form.state} onChange={set("state")} />
            </div>
            <select
              data-testid="quote-coverage-select"
              className={`${inputCls} ${form.coverage_type ? "text-white" : "text-white/30"}`}
              value={form.coverage_type}
              onChange={set("coverage_type")}
            >
              <option value="" disabled>Coverage type *</option>
              {LIFE_OPTIONS.map((o) => (
                <option key={o.name} value={o.name} className="bg-[#0a192f] text-white">{o.name}</option>
              ))}
              <option value="Not sure — help me choose" className="bg-[#0a192f] text-white">Not sure — help me choose</option>
            </select>
            <textarea
              data-testid="quote-message-input"
              rows={4}
              className={inputCls}
              placeholder="Anything else we should know? (optional)"
              value={form.message}
              onChange={set("message")}
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="quote-submit-btn"
              className="btn-sweep w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold-metallic px-8 py-4 font-body text-sm font-semibold tracking-wide text-black disabled:opacity-60"
            >
              {sending ? "Sending..." : "Request My Free Quote"}
              <Send size={16} />
            </button>
            <p className="font-body text-[11px] leading-relaxed text-white/35 text-center">
              By submitting, you agree to be contacted by Heritage Family Protection Agency about coverage options.
              We never sell your information.
            </p>
            <Link
              to="/book-a-call"
              data-testid="quote-book-call-link"
              className="flex items-center justify-center gap-2 font-body text-sm text-[#d4af37] hover:text-[#fcf6ba] transition-colors duration-300"
            >
              <CalendarDays size={15} />
              Prefer to talk it through live? Book a call on our calendar
            </Link>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
